/**
 * Alle Nederlandse UI-tekst, letterlijk uit
 * `docs/handoff/design_handoff_gedenk_delivery/copy.md`. Eén plek, zodat afwijkingen
 * opvallen en de acceptatiecriteria erop te toetsen zijn.
 * Toon: je/jij, kalm, geen uitroeptekens, geen rouwtaal.
 */

import { formatDateInSentence, isTomorrow } from './lib/time';
import type { FailureReasonKey } from './api/types';

export const copy = {
  status: {
    online: 'online',
    offline: 'offline',
    queue: (n: number) => `${n} nog te versturen`,
    lastUpdated: (clock: string) => `Laatst bijgewerkt om ${clock}`,
    queueWaiting: 'wacht op verbinding',
    queueSending: 'wordt verstuurd',
    queueSent: 'verstuurd',
    queueFailed: 'lukt niet',
    earlyPickup: 'vervroegd mee',
    notTaken: 'niet meegenomen',
  },

  login: {
    wordmark: 'Gedenkboekje',
    title: 'Welkom terug',
    intro: 'Log in met je bezorgersaccount.',
    email: 'E-mailadres',
    password: 'Wachtwoord',
    show: 'Toon',
    hide: 'Verberg',
    submit: 'Inloggen',
    submitting: 'Bezig met inloggen…',
    forgot: 'Wachtwoord vergeten',
    invalid: 'Deze combinatie klopt niet. Probeer het nog eens.',
    noDriverRole:
      'Dit account is niet ingesteld als bezorger. Vraag de beheerder om het te regelen.',
    offline: 'Je hebt even verbinding nodig om in te loggen. Zodra je bereik hebt, lukt het.',
  },

  day: {
    subtitle: 'Je rit van vanavond',
    stops: 'stops',
    planned: 'gepland',
    driving: 'rijden',
    expectedDone: 'verwacht klaar',
    pickupPoint: (name: string) => `Ophaalpunt · ${name}`,
    noCar: 'Nog geen auto toegewezen',
    navigateToPickup: 'Navigeer naar het ophaalpunt',
    startPickup: 'Ophalen starten',
    continueRide: 'Verder met de rit',
    empty: 'Je hebt vandaag geen rit.',
    loadError: 'We konden je rit niet ophalen. Probeer het zo nog eens.',
    retry: 'Opnieuw proberen',
    provisional: 'De volgorde is nog voorlopig. De planning rondt hem vóór 16:00 af.',
    settings: 'Instellingen',
    queue: 'Nog te versturen',
  },

  pickupSheet: {
    title: 'Ophalen starten?',
    body: 'Vanaf nu ligt je rit vast. De planning kan er niets meer aan wijzigen.',
    confirm: 'Ja, ophalen starten',
    cancel: 'Nog even niet',
  },

  load: {
    title: 'Ophalen',
    counter: (loaded: number, total: number) => `${loaded} van ${total} geladen`,
    counterWithMissing: (loaded: number, total: number, missing: number) =>
      `${loaded} van ${total} geladen · ${missing} niet meegenomen`,
    destination: (partner: string, city: string) => `${partner} · ${city}`,
    rowAction: 'Ligt er niet',
    scan: 'Scannen',
    manual: 'Handmatig invoeren',
    startRide: 'Rit starten',
    notTakenTitle: 'Niet meegenomen?',
    notTakenBody:
      'De stop blijft in je rit en wordt bij aankomst als niet gelukt vastgelegd. De planning ziet dit.',
    notTakenConfirm: 'Ja, niet meegenomen',
    notTakenCancel: 'Terug',
    empty: 'Er staan vanavond geen pakketten klaar.',
    alreadyLoaded: 'Deze heb je al geladen',
  },

  ride: {
    title: 'De rit',
    progress: (delivered: number, total: number) => `${delivered} van ${total} bezorgd`,
    current: (clock: string) => `Nu naartoe · gepland ${clock}`,
    delay: (minutes: number) => `+${minutes} min`,
    openStop: (n: number) => `Stop ${n} openen`,
    collapsed: (n: number) => `${n} afgehandeld`,
    notNext: 'Dit is niet je volgende stop',
  },

  stop: {
    back: '← De rit',
    header: (n: number, total: number, clock: string) => `Stop ${n} van ${total} · gepland ${clock}`,
    arrived: (clock: string) => `Aangekomen om ${clock}`,
    parcelPending: 'Nog afmelden',
    parcelDelivered: 'Bezorgd',
    parcelCount: (n: number) => `${n} ${n === 1 ? 'pakket' : 'pakketten'} voor dit adres`,
    twoParcels: 'Twee pakketten voor dit adres — scan ze allebei.',
    navigate: 'Navigeer',
    call: 'Bellen',
    arrive: 'Ik ben er',
    deliver: 'Bezorgd melden — scan het label',
    deliverSecond: 'Scan het tweede pakket',
    fail: 'Deze is niet gelukt',
    noPhone: 'Geen nummer bekend',
    noCoordinates: 'Navigeren op adres',
  },

  scanner: {
    subtitle: (n: number) => `stop ${n} · scan het label`,
    subtitleLoad: 'laden · scan de labels',
    close: 'Sluiten',
    hint: 'Richt op de QR-code op het label',
    torch: 'Zaklamp',
    manual: 'Handmatig invoeren',
    slowHint: 'Lukt het niet? Je kunt het nummer ook overtypen.',
    permissionExplain:
      'Om pakketten af te melden scan je de QR-code op het label. Daar heeft de app je camera voor nodig.',
    permissionAllow: 'Camera toestaan',
    permissionManual: 'Liever overtypen',
    denied:
      'Camera staat uit voor deze app. Je kunt hem aanzetten in de instellingen van je telefoon.',
    failed: 'De camera doet het even niet.',
  },

  scanResult: {
    delivered: 'Bezorgd',
    sentAt: (clock: string) => `${clock} · verstuurd`,
    queuedChip: 'Wordt verstuurd zodra je bereik hebt',
    next: (n: number) => `Verder — stop ${n}`,
    nextReturn: 'Verder — terugrit',
    oneMoreParcel: 'Nog 1 pakket voor dit adres',
    undo: 'Toch niet? Draai terug',
    otherStopTitle: (n: number) => `Dit hoort bij stop ${n}`,
    otherStopBody: (current: number, other: number) =>
      `Je staat nu bij stop ${current}. Wil je stop ${other} nu afmelden?`,
    otherStopConfirm: (n: number) => `Ja, stop ${n} afmelden`,
    back: 'Terug',
    alreadyTitle: 'Deze heb je al afgemeld',
    alreadyBody: (clock: string, partner: string) => `Bezorgd om ${clock} bij ${partner}`,
    backToRide: 'Terug naar de rit',
    unknownTitle: 'Niet in je rit van vandaag',
    unknownBody: 'Dit pakket zit niet in je rit. Bel de planning als je twijfelt.',
    callPlanning: 'Bel de planning',
  },

  manual: {
    title: 'Handmatig invoeren',
    label: 'Pakketnummer · laatste 12 tekens',
    explain:
      'Typ de laatste 12 tekens van het label over. Let op: de letter O bestaat niet — dat is altijd een nul die je verkeerd leest.',
    full: 'Volledig nummer intypen ↓',
    fullLabel: 'Volledig pakketnummer',
    submit: 'Afmelden',
    submitLoad: 'Toevoegen',
    remaining: (n: number) => `Nog ${n} ${n === 1 ? 'teken' : 'tekens'}`,
    backToScanner: 'Terug naar scanner',
    notInRoute: 'Dit nummer zit niet in je rit van vandaag.',
    alreadyDelivered: (clock: string) => `Deze heb je al afgemeld om ${clock}.`,
    otherStop: (n: number) => `Dit hoort bij stop ${n} — wil je die afmelden?`,
    callPlanning: 'Bel de planning',
    again: 'Opnieuw',
  },

  fail: {
    back: (n: number) => `← Stop ${n}`,
    title: 'Deze is niet gelukt',
    question: 'Wat is er aan de hand?',
    reasons: {
      noOnePresent: 'Niemand aanwezig',
      closed: 'Gesloten',
      wrongAddress: 'Adres klopt niet',
      parcelDamaged: 'Pakket beschadigd',
    } satisfies Record<FailureReasonKey, string>,
    note: 'Toelichting (niet verplicht)',
    invoiceTitle: 'Toch factureren?',
    invoiceConsequence: 'De ondernemer krijgt hier wél een factuur voor.',
    redeliverTitle: 'Morgen opnieuw langsbrengen?',
    redeliverConsequence: 'Je brengt dit pakket morgenavond opnieuw langs.',
    submit: 'Vastleggen',
    submitDisabled: 'Kies eerst wat er aan de hand is.',
    confirmTitle: 'Vastgelegd',
    confirmSubject: (partner: string, n: number) => `${partner} · stop ${n}`,
    confirmReason: (reason: string) => `Reden: ${reason.toLowerCase()}`,
    confirmInvoiceYes: 'De ondernemer krijgt hier wél een factuur voor',
    confirmInvoiceNo: 'De ondernemer krijgt geen factuur',
    /**
     * Amendement op het contract: `stop.fail` geeft de geplande herbezorgdatum terug. Is dat
     * letterlijk morgen, dan blijft de ontwerpzin staan; anders komt de datum in dezelfde zin.
     * Zie AFWIJKINGEN.md A-1.
     */
    confirmRedeliverYes: (date: string | null, reference: number = Date.now()) =>
      date === null || isTomorrow(date, reference)
        ? 'Je brengt dit pakket morgenavond opnieuw langs'
        : `Je brengt dit pakket ${formatDateInSentence(date)} opnieuw langs`,
    confirmRedeliverNo: 'Geen herbezorging gepland',
    confirmFootnote: 'Klopt dit niet? Alleen de planning kan het corrigeren — bel ze gerust.',
    continue: 'Verder',
    callPlanning: 'Bel de planning',
  },

  ret: {
    title: 'Alle stops gedaan — nu terug',
    body: 'De rit eindigt waar hij begon.',
    leg: (minutes: string, km: string) => `± ${minutes} · ${km}`,
    delivered: 'bezorgd',
    failed: 'niet gelukt',
    navigate: 'Navigeer',
    complete: 'Rit afronden',
    sheetTitle: 'Rit afronden?',
    sheetAllDelivered: (n: number) => `Alle ${n} stops bezorgd. Alles is verstuurd.`,
    sheetMixed: (delivered: number, failed: number) =>
      `${delivered} bezorgd, ${failed} niet gelukt. Alles is verstuurd.`,
    sheetOpen: (n: number, names: string) =>
      `Er staan nog ${n} stops open: ${names}. Weet je zeker dat je de rit wilt afronden?`,
    sheetQueue: (n: number) =>
      `Er staan nog ${n} afmeldingen klaar om te versturen. Dat gebeurt vanzelf zodra je bereik hebt.`,
    sheetFailFirst: 'Eerst als niet gelukt afmelden',
    sheetConfirm: 'Ja, rit afronden',
    sheetCancel: 'Terug',
    doneTitle: 'Je rit zit erop.',
    doneAllDelivered: (n: number) =>
      `Alle ${n} stops bezorgd. Alles is verstuurd. Er hoeft niets meer.`,
    doneMixed: (delivered: number, redelivered: number) =>
      `${delivered} bezorgd, ${redelivered} gaat morgen opnieuw mee. Alles is verstuurd. Er hoeft niets meer.`,
    doneBack: 'Naar vandaag',
  },

  queue: {
    title: 'Nog te versturen',
    close: 'Sluiten',
    intro: 'Alles is al vastgelegd. Versturen gebeurt vanzelf zodra je bereik hebt.',
    itemTitles: {
      'stop.deliver': (partner: string) => `Bezorgd — ${partner}`,
      'stop.arrive': (partner: string) => `Ik ben er — ${partner}`,
      'stop.fail': (partner: string) => `Niet gelukt — ${partner}`,
      'parcel.load': (partner: string) => `Geladen — ${partner}`,
      'parcel.notTaken': (partner: string) => `Niet meegenomen — ${partner}`,
      'route.startPickup': () => 'Ophalen gestart',
      'route.start': () => 'Rit gestart',
      'route.complete': () => 'Rit afgerond',
    },
    reportToPlanning: 'Meld dit bij de planning',
    tryNow: 'Nu proberen',
    emptyTitle: 'Alles is verstuurd.',
    emptyBody: 'Er staat niets meer klaar.',
  },

  settings: {
    title: 'Instellingen',
    close: 'Sluiten',
    sound: 'Geluid bij scan',
    version: (version: string) => `Versie ${version}`,
    logout: 'Uitloggen',
    logoutTitle: 'Uitloggen?',
    logoutQueue: (n: number) =>
      `Er staan nog ${n} afmeldingen klaar om te versturen. Als je uitlogt, blijven ze staan tot je weer inlogt.`,
    logoutConfirm: 'Ja, uitloggen',
    logoutCancel: 'Terug',
  },

  install: {
    title: 'Zet de app op je beginscherm',
    body: 'Tik op Deel, kies Zet op beginscherm en dan Voeg toe. Dan opent de app zonder browserbalk.',
    understood: 'Begrepen',
  },
} as const;

/** Serverreden bij een definitief mislukt wachtrij-item (copy.md §Nog te versturen). */
export const SERVER_REASONS: Record<string, string> = {
  ROUTE_ALREADY_COMPLETED: 'De planning heeft deze rit al afgerond.',
  STOP_NO_LONGER_IN_ROUTE: 'Deze stop staat niet meer in je rit.',
};
